import { createAppRequest } from '@/services/request';
import type {
  MethodologyAiDiscoverItem,
  MethodologyAiDiscoverMode,
  MethodologyLibraryDetail,
  MethodologyLibraryItem,
  MethodologyLibrarySortBy,
  MethodologyLibrarySortOrder,
} from './types/methodology';

const request = createAppRequest();
const BASE = '/bff/methodology-center/v2';

export type ListLibraryItemsParams = {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
  lifecycleStatus?: string;
  sortBy?: MethodologyLibrarySortBy;
  sortOrder?: MethodologyLibrarySortOrder;
};

export async function listLibraryItems(
  library: string,
  params?: ListLibraryItemsParams,
): Promise<{ list: MethodologyLibraryItem[]; total: number }> {
  return request<{ list: MethodologyLibraryItem[]; total: number }>(`${BASE}/${library}`, {
    method: 'GET',
    params,
  });
}

export async function createLibraryItem(
  library: string,
  data: Record<string, unknown>,
): Promise<MethodologyLibraryItem> {
  return request<MethodologyLibraryItem>(`${BASE}/${library}`, { method: 'POST', data });
}

export async function getLibraryItemDetail(
  library: string,
  uuid: string,
): Promise<MethodologyLibraryDetail> {
  return request<MethodologyLibraryDetail>(`${BASE}/${library}/${uuid}`, { method: 'GET' });
}

export async function updateLibraryItem(
  library: string,
  uuid: string,
  data: Record<string, unknown>,
): Promise<MethodologyLibraryItem> {
  return request<MethodologyLibraryItem>(`${BASE}/${library}/${uuid}`, {
    method: 'PUT',
    data,
  });
}

export async function removeLibraryItem(library: string, uuid: string): Promise<void> {
  return request<void>(`${BASE}/${library}/${uuid}`, { method: 'DELETE' });
}

export async function publishLibraryItem(
  library: string,
  uuid: string,
  data?: Record<string, unknown>,
): Promise<MethodologyLibraryItem> {
  return request<MethodologyLibraryItem>(`${BASE}/${library}/${uuid}`, {
    method: 'PUT',
    data: { ...(data ?? {}), status: 'published' },
  });
}

export async function importArticleToLibrary(
  library: string,
  data: Record<string, unknown>,
): Promise<MethodologyLibraryItem> {
  return request<MethodologyLibraryItem>(`${BASE}/${library}/import-article`, {
    method: 'POST',
    data,
  });
}

export async function aiDiscoverLibraryItems(
  library: string,
  data: { mode?: MethodologyAiDiscoverMode; [key: string]: unknown },
): Promise<{ items: MethodologyAiDiscoverItem[]; warnings?: string[]; model?: string | null; provider?: string | null }> {
  return request<{ items: MethodologyAiDiscoverItem[]; warnings?: string[]; model?: string | null; provider?: string | null }>(`${BASE}/${library}/ai-discover`, {
    method: 'POST',
    data,
  });
}

export async function aiMineLibraryItemTags(
  library: string,
  uuid: string,
  data?: Record<string, unknown>,
): Promise<{ suggestedNames: string[]; model?: string | null }> {
  return request<{ suggestedNames: string[]; model?: string | null }>(`${BASE}/${library}/${uuid}/mine-tags`, {
    method: 'POST',
    data,
  });
}

export async function aiMatchLibraryItemTags(
  library: string,
  uuid: string,
  data?: Record<string, unknown>,
): Promise<{ matchedTagUuids: string[]; model?: string | null }> {
  return request<{ matchedTagUuids: string[]; model?: string | null }>(`${BASE}/${library}/${uuid}/match-tags`, {
    method: 'POST',
    data,
  });
}
